import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core'
import {finalize, firstValueFrom, Subject, takeUntil} from 'rxjs'
import {Wallet} from '../../models/wallet'
import {NgClass, NgForOf, NgIf} from '@angular/common'
import {FormsModule} from '@angular/forms'
import {User} from '../../models/user'
import {WalletService} from '../../services/wallet.service'
import {ScriptService} from '../../services/script.service'
import {CreditCardExpirationDirective} from '../directives/credit-card-expiration.directive'
import {CreditCardNumberDirective} from '../directives/credit-card-number.directive'
import {AuthService} from '../../services/auth.service'
import {CreditCardCCVDirective} from '../directives/credit-card-ccv.directive'

// Loaded dynamically from scripts
declare var Stripe: any
declare var braintree: any

enum PaymentMethod {
  WALLET,
  CREDIT_CARD,
  PAYPAL,
}

@Component({
  selector: 'app-payment-form',
  imports: [
    NgIf,
    NgClass,
    FormsModule,
    CreditCardNumberDirective,
    CreditCardExpirationDirective,
    CreditCardCCVDirective,
    NgForOf
  ],
  templateUrl: './payment-form.component.html',
  styleUrl: './payment-form.component.scss'
})
export class PaymentFormComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>()

  protected readonly PaymentMethod = PaymentMethod

  @Input() paypalInstructions: string | null = null
  @Input() showExistingWallets: boolean = false
  @Input() companyId: string | null = null

  @Output() onPaymentFormLoaded = new EventEmitter<boolean>()
  @Output() onPaymentFormValid = new EventEmitter<boolean>()

  // Payment method
  paymentMethod: PaymentMethod = PaymentMethod.CREDIT_CARD


  // Customer
  currentUser: User | null = null

  // Wallet
  isWalletLoading = false
  isWalletSelectedPreviousFailed = false
  wallets: Wallet[] = []
  walletSelected: Wallet | null = null

  // Credit card
  isCreditCardLoading = false
  creditCardNumber: string | null = null
  creditCardExpiration: string | null = null
  creditCardCCV: string | null = null
  stripePublishableKey: string | null = null

  // Paypal
  isPaypalLoading = false
  isPaypalLoaded = false
  paypalNonce: string | null = null
  @ViewChild('paypalButton') paypalButton!: ElementRef

  constructor(private scriptService: ScriptService,
              private authService: AuthService,
              private walletService: WalletService) {
  }

  async ngOnInit() {
    this.authService.currentUser$.pipe(takeUntil(this.onDestroy$)).subscribe(currentUser => {
      this.currentUser = currentUser
    })

    // Load payment scripts
    const paymentScriptsPromise = this.scriptService.load("stripe", "braintree")

    // Load stripe
    const stripePublishableKeyPromise = firstValueFrom(this.walletService.generateStripePublishableKey().pipe(takeUntil(this.onDestroy$)))

    // Load wallets
    const walletsPromise = this.showExistingWallets
      ? this.companyId
        ? firstValueFrom(this.walletService.findByCompanyId(this.companyId).pipe(takeUntil(this.onDestroy$)))
        : firstValueFrom(this.walletService.findByUser().pipe(takeUntil(this.onDestroy$)))
      : Promise.resolve([])

    this.isCreditCardLoading = true
    this.isWalletLoading = true
    await Promise.all([paymentScriptsPromise, stripePublishableKeyPromise, walletsPromise]).then(results => {
      const paymentScripts = results[0]
      const stripePublishableKey = results[1]
      const wallets = results[2]

      // Stripe
      this.stripePublishableKey = stripePublishableKey.key

      // Wallet
      this.wallets = wallets
      this.initializeWallets()

    }).finally(() => {
      this.isCreditCardLoading = false
      this.isWalletLoading = false

      this.onPaymentFormLoaded.emit(true)
    })
  }

  ngOnDestroy() {
    this.onDestroy$.next(true)
    this.onDestroy$.complete()
  }

  selectPaymentType(paymentType: PaymentMethod) {
    this.paymentMethod = paymentType

    if (paymentType === PaymentMethod.PAYPAL && !this.isPaypalLoaded) {
      this.reloadPaypalButton()
    }

    this.validate()
  }

  selectWallet(wallet: Wallet) {
    this.walletSelected = wallet
    this.isWalletSelectedPreviousFailed = !!wallet.failed;
  }

  initializeWallets() {
    setTimeout(() => {
      for (const wallet of this.wallets) {
        if (wallet.isDefault) {
          this.walletSelected = wallet
          this.isWalletSelectedPreviousFailed = !!wallet.failed;
          this.selectPaymentType(PaymentMethod.WALLET)
        }
      }
    }, 0)
  }

  onCompanyCreateOrRetrieveWallet(companyId: string) {
    this.companyId = companyId;
    return this.onCreateOrRetrieveWallet()
  }

  onCreateOrRetrieveWallet() {
    return new Promise((resolve, reject) => {

      if (!this.isValid()) {
        return reject("We had a problem adding your payment method. Please try again or contact support.")
      }

      if (!this.currentUser) {
        return reject("We had a problem adding your payment method. Please try again or contact support.")
      }

      // Wallet
      if (this.paymentMethod === PaymentMethod.WALLET) {
        const walletId = this.walletSelected!._id
        return firstValueFrom(this.walletService.setDefault(walletId)).then(() => {
          return resolve(walletId)
        })
      }

      // Credit Card
      if (this.paymentMethod === PaymentMethod.CREDIT_CARD) {
        Stripe.setPublishableKey(this.stripePublishableKey)

        const cardNumber = this.creditCardNumber!.split(' ').join('')
        const cardExpMonth = this.creditCardExpiration!.split("/")[0].trim()
        const cardExpYear = this.creditCardExpiration!.split("/")[1].trim()
        const cardCCV = this.creditCardCCV!

        Stripe.card.createToken(
          {
            number: cardNumber,
            cvc: cardCCV,
            exp_month: cardExpMonth,
            exp_year: cardExpYear,
          },

          (status: number, res: any) => {
            if (status === 200 && res.card) {

              const cardToken = res.id
              const email = this.currentUser!.email!
              const description = this.currentUser!._id + ' ' + this.currentUser!.firstName + ' ' + this.currentUser!.lastName

              if (this.companyId) {
                this.walletService
                  .createStripeCompanyWallet(this.companyId, email, description, cardToken)
                  .pipe(takeUntil(this.onDestroy$))
                  .subscribe({
                    next: (createdWallet: Wallet) => {
                      const walletId = createdWallet._id
                      this.walletService.refresh$.next(walletId)
                      return resolve(walletId)
                    },
                    error: () => {
                      return reject('We had a problem adding your payment method. Please try again or contact support.')
                    }
                  })
              } else {
                this.walletService
                  .createStripeCustomerWallet(email, description, cardToken)
                  .pipe(takeUntil(this.onDestroy$))
                  .subscribe({
                    next: (createdWallet: Wallet) => {
                      const walletId = createdWallet._id
                      this.walletService.refresh$.next(walletId)
                      return resolve(walletId)
                    },
                    error: () => {
                      return reject('We had a problem adding your payment method. Please try again or contact support.')
                    }
                  })
              }
            } else {
              return reject(res.error.message)
            }
          },
        )
      }

      if (this.paymentMethod === PaymentMethod.PAYPAL) {
        if (this.companyId) {
          this.walletService
            .createBraintreeCompanyWallet(this.companyId, this.paypalNonce!)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (createdWallet: Wallet) => {
                const walletId = createdWallet._id
                this.walletService.refresh$.next(walletId)
                return resolve(walletId)
              },
              error: () => {
                return reject('We had a problem adding your payment method. Please try again or contact support.')
              }
            })
        } else {
          this.walletService
            .createBraintreeCustomerWallet(this.paypalNonce!)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (createdWallet: Wallet) => {
                const walletId = createdWallet._id
                this.walletService.refresh$.next(walletId)
                return resolve(walletId)
              },
              error: () => {
                return reject('We had a problem adding your payment method. Please try again or contact support.')
              }
            })
        }
      }

    })
  }

  reloadPaypalButton() {
    const paypalButtonElement = this.paypalButton.nativeElement as HTMLElement
    while (paypalButtonElement.firstChild) {
      paypalButtonElement.removeChild(paypalButtonElement.firstChild)
    }

    this.isPaypalLoading = true
    this.walletService
      .generateBraintreeClientToken()
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.isPaypalLoading = false
          this.isPaypalLoaded = true
        }))
      .subscribe(
        (response: any) => {
          const token = response.token
          braintree.setup(token, 'custom', {
            paypal: {container: 'paypal-button'},
            onPaymentMethodReceived: (response: any) => { // TODO - this callback is called twice...
              this.paypalNonce = response.nonce
              this.validate()
            },
            onError: (error: any) => {
              console.error(error)
            },
          })
        }
      )
  }

  isValid() {
    switch (this.paymentMethod) {
      case PaymentMethod.WALLET:
        return this.isWalletValid()
      case PaymentMethod.CREDIT_CARD:
        return this.isCreditCardValid()
      case PaymentMethod.PAYPAL:
        return this.isPaypalValid()
      default:
        return false
    }
  }

  validate() {
    this.onPaymentFormValid.emit(this.isValid())
  }

  isWalletValid(): boolean {
    if (this.wallets.length === 0) return false
    if (this.walletSelected === null) return false

    return true
  }

  isCreditCardValid(): boolean {
    if (!this.creditCardNumber) return false
    if (!this.creditCardExpiration) return false
    if (!this.creditCardCCV) return false

    // 1111 2222 3333 4444
    if (this.creditCardNumber.split(' ').join('').length != 16) return false

    // MM / YYYY
    if (this.creditCardExpiration.split("/").length != 2) return false

    // MM
    if (this.creditCardExpiration.split("/")[0].trim().length != 2) return false

    // YY or YYYY
    if (!(this.creditCardExpiration?.split("/")[1].trim().length === 2 || this.creditCardExpiration?.split("/")[1].trim().length === 4)) return false

    // 111 or 1111
    if (this.creditCardCCV.length < 3) return false

    return true
  }

  isPaypalValid() {
    if (!this.paypalNonce) return false

    return true
  }

}
